/**
 * Cubing System Module
 * Pure functions for potential rolling, cube application, and validation
 */

import {
  CUBING_CONFIG,
  CUBE_TYPES,
  POTENTIAL_LINES,
  SLOT_POTENTIAL_STATS,
  getNextGrade,
  getLinesForGrade,
} from '../../data/cubing';

/**
 * Roll potential lines for a given grade
 * @param {string} grade - Potential grade (rare, epic, unique, legendary)
 * @param {string} slot - Optional equipment slot for filtering stats
 * @returns {Array} Array of rolled potential lines
 */
export function rollPotentialLines(grade, slot = null) {
  let pool = POTENTIAL_LINES[grade];
  if (!pool || pool.length === 0) return [];

  // Filter pool by slot if provided
  if (slot && SLOT_POTENTIAL_STATS[slot]) {
    const allowedStats = SLOT_POTENTIAL_STATS[slot];
    pool = pool.filter(line => allowedStats.includes(line.stat));
    // Fallback to full pool if filtering results in empty pool
    if (pool.length === 0) {
      pool = POTENTIAL_LINES[grade];
    }
  }

  const numLines = getLinesForGrade(grade);
  const lines = [];

  // Calculate total weight
  const totalWeight = pool.reduce((sum, line) => sum + line.weight, 0);

  for (let i = 0; i < numLines; i++) {
    // Weighted random selection
    let roll = Math.random() * totalWeight;
    let selectedLine = pool[0];

    for (const line of pool) {
      roll -= line.weight;
      if (roll <= 0) {
        selectedLine = line;
        break;
      }
    }

    // Add the selected line (copy without weight)
    lines.push({
      stat: selectedLine.stat,
      type: selectedLine.type,
      value: selectedLine.value,
    });
  }

  return lines;
}

/**
 * Check if cube can upgrade the current grade
 * @param {string|Object} cubeOrId - Cube ID or cube type definition
 * @param {string} currentGrade - Current potential grade
 * @returns {Object} { canUpgrade: boolean, nextGrade: string|null, chance: number }
 */
export function getGradeUpInfo(cubeOrId, currentGrade) {
  const cube = typeof cubeOrId === 'string' ? CUBE_TYPES[cubeOrId] : cubeOrId;

  if (!cube || !cube.canGradeUp) {
    return { canUpgrade: false, nextGrade: null, chance: 0 };
  }

  const nextGrade = getNextGrade(currentGrade);
  if (!nextGrade) {
    return { canUpgrade: false, nextGrade: null, chance: 0 };
  }

  const chance = cube.gradeUpRates[nextGrade] || 0;
  return {
    canUpgrade: chance > 0,
    nextGrade,
    chance,
  };
}

/**
 * Apply a cube to equipment and get the result
 * @param {Object} item - Equipment item with current potential
 * @param {string} cubeId - Cube ID
 * @returns {Object} Result with newGrade, newLines, gradeUp (boolean), newItem
 */
export function applyCubeToItem(item, cubeId) {
  const cube = CUBE_TYPES[cubeId];
  if (!cube) {
    return { error: 'Invalid cube type' };
  }

  // Initialize potential if item doesn't have it
  const currentGrade = item.potentialGrade || 'rare';
  const wasInitialized = !item.potentialGrade;

  // Check for grade up
  const gradeUpInfo = getGradeUpInfo(cube, currentGrade);
  let newGrade = currentGrade;
  let gradeUp = false;

  if (gradeUpInfo.canUpgrade && Math.random() < gradeUpInfo.chance) {
    newGrade = gradeUpInfo.nextGrade;
    gradeUp = true;
  }

  // Roll new potential lines for the (potentially upgraded) grade
  // Pass item.slot to filter potential pool by equipment slot
  const newLines = rollPotentialLines(newGrade, item.slot);

  // Create new item with updated potential
  const newItem = {
    ...item,
    potentialGrade: newGrade,
    potentialLines: newLines,
  };

  return {
    newGrade,
    newLines,
    gradeUp,
    previousGrade: wasInitialized ? null : currentGrade,
    previousLines: item.potentialLines || [],
    newItem,
  };
}

/**
 * Check if a cube can be applied to an item
 * @param {Object} item - Equipment item
 * @param {string} cubeId - Cube ID
 * @param {number} cubeCount - Number of cubes available
 * @returns {Object} { canApply: boolean, reason: string }
 */
export function canApplyCube(item, cubeId, cubeCount) {
  // Check if we have cubes
  if (cubeCount <= 0) {
    return { canApply: false, reason: 'No cubes available' };
  }

  // Check if cube type exists
  const cube = CUBE_TYPES[cubeId];
  if (!cube) {
    return { canApply: false, reason: 'Invalid cube type' };
  }

  // Check if item is equipment
  if (!item || !item.slot) {
    return { canApply: false, reason: 'Not a valid equipment item' };
  }

  // Check if item has potential (initialize if not)
  // Items without potential will get Rare grade when first cubed

  // Check if already at max grade for cubes that can't upgrade
  if (item.potentialGrade === 'legendary' && !cube.canGradeUp) {
    // Can still reroll lines even at max grade
  }

  return { canApply: true, reason: '' };
}

/**
 * Initialize potential on an item that doesn't have it
 * @param {Object} item - Equipment item
 * @returns {Object} Item with initialized potential
 */
export function initializePotential(item) {
  if (item.potentialGrade) return item;

  return {
    ...item,
    potentialGrade: 'rare',
    potentialLines: rollPotentialLines('rare'),
  };
}

/**
 * Calculate total potential bonuses from an item
 * @param {Object} item - Equipment item with potential
 * @returns {Object} Accumulated bonuses by stat
 */
export function calculatePotentialBonuses(item) {
  const bonuses = {
    STR: 0,
    DEX: 0,
    INT: 0,
    LUK: 0,
    attack: 0,
    defense: 0,
    health: 0,
    allStats: 0,
    critChance: 0,
    critDamage: 0,
    bossDamage: 0,
    ignoreDef: 0,
  };

  if (!item?.potentialLines) return bonuses;

  item.potentialLines.forEach(line => {
    if (bonuses.hasOwnProperty(line.stat)) {
      bonuses[line.stat] += line.value;
    }
  });

  return bonuses;
}

/**
 * Get display info for an item's potential
 * @param {Object} item - Equipment item
 * @returns {Object} Display info with grade, lines, etc.
 */
export function getPotentialDisplayInfo(item) {
  if (!item?.potentialGrade) {
    return {
      hassPotential: false,
      grade: null,
      gradeInfo: null,
      lines: [],
      formattedLines: [],
    };
  }

  const gradeInfo = CUBING_CONFIG.gradeInfo[item.potentialGrade];

  return {
    hasPotential: true,
    grade: item.potentialGrade,
    gradeInfo,
    lines: item.potentialLines || [],
  };
}
